import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "../styles/HelpPage.css";
import { IconMail } from "../components/Icons";

function HelpPage() {

  const sections = [
    {
      id: "enable-location",
      title: "1. Enable Current Location",
      content: (
        <>
          <h3>Mobile</h3>

          <p><b>Step 1: Turn ON Location (GPS)</b></p>
          <ul>
            <li>Open Settings</li>
            <li>Tap Location</li>
            <li>Turn ON Location (Enable GPS)</li>
          </ul>

          <p><b>Step 2: Allow Browser Permission</b></p>
          <ul>
            <li>Open Chrome browser</li>
            <li>Visit the website</li>
            <li>Tap <b>Allow</b> when the location popup appears</li>
          </ul>

          <p><b>Step 3: Use Current Location</b></p>
          <ul>
            <li>Tap <b>Use Current Location</b> on the home page</li>
            <li>Businesses within 50 km will be shown, sorted nearest first</li>
          </ul>

          <div className="help-divider" />

          <h3>Desktop (PC / Laptop)</h3>
          <ul>
            <li><p><b>Step 1:</b> Open the website in Chrome</p></li>
            <li><p><b>Step 2:</b> Click <b>Allow</b> in the location popup</p></li>
            <li><p><b>Step 3:</b> Location is detected automatically</p></li>
          </ul>

          <div className="help-divider" />

          <h3>If Location is Not Working</h3>

          <h4>Mobile Fix</h4>
          <ul>
            <li>Turn ON your phone Location (GPS)</li>
            <li>Open Chrome browser</li>
            <li>Go to Chrome Settings → Site settings → Location</li>
            <li>Set Location to <b>Allow</b></li>
            <li>Refresh the page</li>
          </ul>

          <h4>Desktop Fix</h4>
          <ul>
            <li>Chrome → Settings → Site Settings → Location → Allow</li>
            <li>Reload page</li>
          </ul>
        </>
      ),
    },
    {
      id: "register-business",
      title: "2. Register Business",
      content: (
        <>
          <p><b>Requirements before registering:</b></p>
          <ul>
            <li>Location access must be enabled</li>
            <li>Valid 10-digit Indian mobile number (e.g. 9876543210)</li>
          </ul>

          <p><b>Steps:</b></p>
          <ul>
            <li>Click <b>Register Business</b> in the header</li>
            <li>Allow location access when prompted</li>
            <li>Fill in business details — Name, Category, Services, City</li>
            <li>Enter owner name and phone number</li>
            <li>Optionally add email, website, and address</li>
            <li>Upload up to 3 clear business images (optional)</li>
            <li>Click <b>Register Business</b></li>
          </ul>

          <p><b>After registration:</b></p>
          <ul>
            <li>Business is live immediately</li>
            <li>Appears in search and nearby results</li>
            <li>Customers can call or WhatsApp directly from the listing</li>
          </ul>

          <p><b>Phone number formats accepted:</b></p>
          <ul>
            <li>9876543210</li>
            <li>+919876543210</li>
            <li>919876543210</li>
          </ul>
        </>
      ),
    },
    {
      id: "search-businesses",
      title: "3. Search Businesses",
      content: (
        <>
          <p><b>Search by keyword:</b></p>
          <ul>
            <li>Type a business name, category, or service in the search box</li>
            <li>Select your city from the dropdown</li>
            <li>Click <b>Search</b></li>
          </ul>

          <p><b>Search by location:</b></p>
          <ul>
            <li>Click <b>Use Current Location</b></li>
            <li>Allow GPS access when prompted</li>
            <li>Businesses within 50 km will be shown, nearest first</li>
          </ul>

          <p><b>View business details:</b></p>
          <ul>
            <li>Click on any business card to open details</li>
            <li>See full information — services, address, images</li>
            <li>Use Call, WhatsApp, or Map buttons to connect</li>
            <li>Press <b>Escape</b> or tap outside to close</li>
          </ul>
        </>
      ),
    },
    {
      id: "category-filter",
      title: "4. Using Category Filter",
      content: (
        <>
          <p>Use the category buttons below the search bar to narrow down results.</p>

          <p><b>Available categories:</b></p>
          <ul>
            <li>Electronics</li>
            <li>Fashion</li>
            <li>Grocery</li>
            <li>Furniture</li>
            <li>Automobile</li>
            <li>Services</li>
            <li>Others</li>
          </ul>

          <p><b>How to use:</b></p>
          <ul>
            <li>Tap any category button to filter the current results</li>
            <li>Tap <b>All</b> to remove the filter and see all businesses</li>
            <li>Category filter works together with both search and location results</li>
            <li>On mobile, scroll the category bar horizontally to see all options</li>
          </ul>
        </>
      ),
    },
    {
      id: "important-notes",
      title: "5. Important Notes",
      content: (
        <>
          <ul>
            <li>Location must be enabled for accurate nearby results</li>
            <li>Allow browser location permissions when prompted</li>
            <li>Ensure an active internet connection</li>
            <li>Refresh the page if results are not loading</li>
            <li>Each phone number can only be registered once</li>
            <li>Business images are resized automatically before upload</li>
          </ul>
        </>
      ),
    },
  ];

  const [searchQuery, setSearchQuery] = useState("");
  const [activeSectionId, setActiveSectionId] = useState(sections[0].id);

  const filteredSections = (() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return sections;
    return sections.filter((section) => section.title.toLowerCase().includes(query));
  })();

  // When search narrows results and the active section is no longer visible,
  // automatically select the first matching section.
  const activeSection = (() => {
    const current = sections.find((s) => s.id === activeSectionId);
    if (current && filteredSections.some((s) => s.id === activeSectionId)) {
      return current;
    }
    return filteredSections[0] || sections[0];
  })();

  return (
    <div className="help-page">
      <Helmet>
        <title>Help Center – BusinessNearMe</title>
        <meta name="description" content="Get help with BusinessNearMe. Learn how to enable location, register a business and search for local services in Tamil Nadu." />
        <link rel="canonical" href="https://businessnearme.in/help" />
      </Helmet>
      <aside className="help-sidebar">
        <div className="help-sidebar-header">
          <h2>Help Center</h2>
          <p>Search sections or choose a topic.</p>
        </div>

        <div className="help-sidebar-search">
          <input
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search sections"
            aria-label="Search help sections"
          />
        </div>

        <nav className="help-sidebar-nav">
          {filteredSections.map((section) => (
            <button
              key={section.id}
              className={`help-sidebar-link ${section.id === activeSectionId ? "active" : ""}`}
              onClick={() => setActiveSectionId(section.id)}
            >
              {section.title}
            </button>
          ))}
          {filteredSections.length === 0 && (
            <p className="help-sidebar-empty">No sections match your search.</p>
          )}
        </nav>
      </aside>

      <main className="help-main">
        <div className="help-main-card">
          <header className="help-main-header">
            <div className="help-main-meta">
              <h1>Help Center Guide</h1>
              <Link to="/" className="help-back-button">← Back to Home</Link>
            </div>
          </header>

          <article className="help-content">
            <h2>{activeSection.title}</h2>
            {activeSection.content}
          </article>

          <div className="help-footer-note">
            <p>End of Guide</p>
          </div>

          <section className="help-support-card">
            <h2>Need Help or Support?</h2>
            <p>Our team provides professional Web and Mobile Development services at budget-friendly pricing.</p>
            <p>If you need any assistance, support, or business inquiries, contact us:</p>
            <p className="help-support-contact">
              <span className="icon-row">
                <IconMail size={14} />
                <a
                  href="https://mail.google.com/mail/?view=cm&fs=1&to=businessnearme.official@gmail.com&su=Support%20Request"
                  target="_blank"
                  rel="noreferrer"
                  className="email-link"
                >
                  Email Us
                </a>
              </span>
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}

export default HelpPage;
